import type { BoardDoc } from '../doc/BoardDoc';
import { newId } from '../doc/BoardDoc';
import type { Item } from '../doc/schema';

/**
 * The items clipboard. One copy lands in three places so pasting works everywhere:
 *   - memory: the same tab (both panes of a split view share it)
 *   - localStorage: other tabs of this browser, and after a reload
 *   - the system clipboard, via the copy/cut events: a private `application/x-vds-items` entry
 *     with the JSON, plus text/plain with the items' text (or a marker when there is none)
 * Pasting prefers the system clipboard, so copying something else in between wins as it should;
 * the stamp ties the three copies together so the fullest one is used.
 */

export interface ClipboardPayload {
  format: 'vds-items';
  version: 1;
  /** Identifies one copy action across the three stores. */
  stamp: string;
  /** When it was copied (ms since epoch); the newer of memory and localStorage wins. */
  at: number;
  /** Board the items came from (assets are cloned when pasting elsewhere). */
  boardId: string;
  /** What the items say, for pasting into other apps. */
  text: string;
  items: Item[];
}

export const CLIPBOARD_MIME = 'application/x-vds-items';
const STORAGE_KEY = 'vds:clipboard';
const MARKER = 'vds:items:';
/** localStorage keeps a copy this large at most (well under its quota). */
const MAX_STORED_BYTES = 3_500_000;

let memory: ClipboardPayload | null = null;

/** Plain-text view of an item, for the text/plain half of the clipboard. */
function plainTextOf(item: Item): string {
  switch (item.type) {
    case 'note':
    case 'title':
      return item.props.text;
    case 'link':
      return item.props.url;
    case 'file':
      return item.props.name;
    case 'frame':
    case 'boardcard':
      return item.props.title;
    case 'kanban':
      return item.props.columns.map((c) => [c.title, ...c.cards.map((k) => `- ${k.text}`)].join('\n')).join('\n\n');
    case 'richtext':
    case 'table':
    case 'todo':
      return textOfNode(item.props.contentJson);
    default:
      return '';
  }
}

/** Text of a ProseMirror JSON tree: block nodes on their own lines. */
function textOfNode(node: unknown): string {
  if (!node || typeof node !== 'object') return '';
  const n = node as { type?: string; text?: string; content?: unknown[] };
  if (typeof n.text === 'string') return n.text;
  const parts = (n.content ?? []).map(textOfNode);
  const block = n.type === 'paragraph' || n.type === 'heading' || n.type === 'listItem' || n.type === 'taskItem' || n.type === 'tableRow';
  return block ? parts.join('') + '\n' : parts.join('');
}

/** Snapshots items for the clipboard. Rich bodies need the rich module loaded (see actions.copySelection). */
export function snapshotForClipboard(board: BoardDoc, ids: Iterable<string>, stamp = newId()): ClipboardPayload {
  const items = board.snapshotItems(ids);
  const text = items
    .map(plainTextOf)
    .map((t) => t.trim())
    .filter(Boolean)
    .join('\n\n');
  return { format: 'vds-items', version: 1, stamp, at: Date.now(), boardId: board.boardId, text, items };
}

/** Keeps a copy in memory and, when it fits, in localStorage for other tabs. */
export function rememberClipboard(payload: ClipboardPayload): void {
  memory = payload;
  try {
    const json = JSON.stringify(payload);
    if (json.length <= MAX_STORED_BYTES) localStorage.setItem(STORAGE_KEY, json);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode or quota: the tab-local copy still works */
  }
}

/** The copy this tab made within the last moments (the copy event follows the keydown that made it). */
export function recentClipboard(withinMs: number): ClipboardPayload | null {
  return memory && Date.now() - memory.at <= withinMs ? memory : null;
}

function parsePayload(raw: string | null | undefined): ClipboardPayload | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<ClipboardPayload>;
    if (p && p.format === 'vds-items' && Array.isArray(p.items) && typeof p.stamp === 'string') {
      return { ...p, at: typeof p.at === 'number' ? p.at : 0 } as ClipboardPayload;
    }
  } catch {
    /* not ours */
  }
  return null;
}

/** The newest copy this browser holds: this tab's, or one another tab left in localStorage. */
export function readStoredClipboard(): ClipboardPayload | null {
  let stored: ClipboardPayload | null = null;
  try {
    stored = parsePayload(localStorage.getItem(STORAGE_KEY));
  } catch {
    /* unavailable */
  }
  if (!memory) return stored;
  if (!stored || stored.stamp === memory.stamp) return memory;
  return stored.at > memory.at ? stored : memory;
}

export function hasStoredClipboard(): boolean {
  return readStoredClipboard() !== null;
}

/** Fills the copy/cut event with the payload (private JSON entry plus plain text). */
export function writeToClipboardEvent(e: ClipboardEvent, payload: ClipboardPayload): void {
  const data = e.clipboardData;
  if (!data) return;
  data.setData(CLIPBOARD_MIME, JSON.stringify(payload));
  data.setData('text/plain', payload.text || MARKER + payload.stamp);
}

/**
 * The items a paste event carries, if any. The private entry wins; a browser that drops private
 * entries still delivers our plain text, which leads back to the stored copy; an empty
 * clipboard (or a blocked write) falls back to the stored copy too. Foreign text yields null.
 */
export function clipboardFromEvent(e: ClipboardEvent): ClipboardPayload | null {
  const data = e.clipboardData;
  const own = parsePayload(data?.getData(CLIPBOARD_MIME));
  const stored = readStoredClipboard();
  // the stored copy of the same action may be fuller (rich bodies serialised after the event)
  if (own) return stored && stored.stamp === own.stamp ? stored : own;
  const text = data?.getData('text/plain') ?? '';
  if (text.startsWith(MARKER)) return stored && stored.stamp === text.slice(MARKER.length) ? stored : null;
  if (!text) return stored;
  return stored && text === stored.text ? stored : null;
}
